import { useCallback, useRef, useState } from 'react';

import { useCurrentUser, useView } from '@/hooks';
import { AppMessageSeverities } from '@/types';
import { Alert, Button, CircularProgress, Typography } from '@/ui';

import {
  actionRecordCanCancel,
  actionRecordCanReschedule,
} from '../appointments/appointmentActionRecord';
import { platformAdminCalendarAppointmentRecord } from '../appointments/calendarAppointmentProjection';
import { legacyAppSelectedPair } from '../appointments/momentsSelectedPairContract';
import { useAppointmentClock } from '../appointments/useNativeAppointmentRows';

import AdminMemberSessionTimeline from './AdminMemberSessionTimeline';
import OpsMemberSessionActions from './OpsMemberSessionActions';
import { useAdminMemberSessions } from './useAdminMemberSessions';

import type { OpsMemberSessionPendingAction } from './OpsMemberSessionActions';
import type { AdminMemberSessionRow } from './composeAdminMemberSessionRows';
import type { UseAdminMemberSessionsOptions } from './useAdminMemberSessions';
import type { ReactNode } from 'react';

import SessionsLayout from '@/components/Layouts/Sessions';
import { useLazyGetMomentsSelectedPairAppointmentQuery } from '@/store/api/momentsAppointmentApi';
import { useAppDispatch } from '@/store/hooks';
import { setAppMessage } from '@/store/slices/appSlice';
import { canAccessPlatformAdminCalendar } from '@/types/user';

const AdminMemberSessions = (props: UseAdminMemberSessionsOptions) => {
  const { enabled, timeZone } = props;
  const { refetch: handleRetry, ...result } = useAdminMemberSessions(props);
  const now = useAppointmentClock(enabled !== false);
  const dispatch = useAppDispatch();
  const [getDetail] = useLazyGetMomentsSelectedPairAppointmentQuery();
  const [loadingRowKey, setLoadingRowKey] = useState<string | null>(null);
  const [pending, setPending] = useState<OpsMemberSessionPendingAction | null>(
    null,
  );
  const nonce = useRef(0);

  // MEN-5338: the history read carries no action capabilities (MEN-5211), so
  // read the authoritative selected-pair appointment on click. It also carries
  // the version and series scopes the Ops drawers need.
  const openAction = useCallback(
    async (
      action: OpsMemberSessionPendingAction['action'],
      row: AdminMemberSessionRow,
      target: HTMLElement,
    ) => {
      if (loadingRowKey) return;
      if (row.source === 'legacy') {
        // Rails-owned Moments series act through the Ops Session routes; the
        // drawer loads Rails' own options, so there is nothing to read first.
        nonce.current += 1;
        setPending({
          action,
          coachId: row.session.coachId,
          nonce: nonce.current,
          returnFocus: () => target.focus(),
          subject: { session: row.session, source: 'session' },
        });
        return;
      }
      const { appointment } = row;
      const coachId = appointment.expectedIdentities.coach.railsUserId;
      setLoadingRowKey(row.key);
      try {
        const detail = await getDetail({
          ...legacyAppSelectedPair(
            coachId,
            appointment.expectedIdentities.member.railsUserId,
          ),
          appointmentId: appointment.id,
        }).unwrap();
        // The selected-pair detail is the platform-admin projection under its
        // own contract name.
        const subject = platformAdminCalendarAppointmentRecord({
          ...detail,
          contractVersion: 'platform-admin-appointment.v1',
        });
        const allowed =
          action === 'cancel'
            ? actionRecordCanCancel(subject)
            : actionRecordCanReschedule(subject);
        if (!allowed) {
          dispatch(
            setAppMessage({
              message: "This session can't be changed right now.",
              open: true,
              severity: AppMessageSeverities.Warning,
            }),
          );
          return;
        }
        nonce.current += 1;
        setPending({
          action,
          coachId,
          nonce: nonce.current,
          returnFocus: () => target.focus(),
          subject,
        });
      } catch {
        dispatch(
          setAppMessage({
            message: 'Unable to load this session. Please try again.',
            open: true,
            severity: AppMessageSeverities.Error,
          }),
        );
      } finally {
        setLoadingRowKey(null);
      }
    },
    [dispatch, getDetail, loadingRowKey],
  );

  // Ops selected-pair mutations invalidate the platform-admin tags, not this
  // page's member-scope tag, so refresh the history explicitly.
  const handleDone = useCallback(() => {
    setPending(null);
    handleRetry();
  }, [handleRetry]);

  return (
    <>
      <AdminMemberSessionTimeline
        {...result}
        now={now}
        pendingRowKey={loadingRowKey}
        timeZone={timeZone}
        onCancel={(row, target) => {
          openAction('cancel', row, target).catch(() => undefined);
        }}
        onReschedule={(row, target) => {
          openAction('reschedule', row, target).catch(() => undefined);
        }}
        onRetry={handleRetry}
      />
      {pending && (
        <OpsMemberSessionActions
          key={`${pending.coachId}:${pending.subject.session.id}:${pending.action}:${pending.nonce}`}
          pending={pending}
          timeZone={timeZone}
          onDone={handleDone}
        />
      )}
    </>
  );
};

// Keep the legacy controllers unmounted until the current viewer is resolved.
export const MemberSessionsPage = ({ children }: { children: ReactNode }) => {
  const {
    currentData: viewer,
    isUserLoading,
    isFetching,
    isError,
  } = useCurrentUser();
  const {
    isOverview,
    selectedUserId,
    selectedUser,
    isViewError,
    isViewFetching,
    isViewLoading,
    refetchSelectedUser: handleRetrySelectedUser,
  } = useView();
  let content = children;
  if (isError) {
    content = (
      <Alert severity="error">
        Unable to verify access to member sessions.
      </Alert>
    );
  } else if (isUserLoading || isFetching || !viewer?.id) {
    content = (
      <CircularProgress aria-label="Loading session access" size={24} />
    );
  } else if (isOverview && canAccessPlatformAdminCalendar(viewer)) {
    if (!selectedUserId) {
      content = <Alert severity="error">No member selected.</Alert>;
    } else if (isViewError) {
      content = (
        <Alert
          action={
            <Button
              aria-label="Retry selected member"
              onClick={handleRetrySelectedUser}
            >
              Retry
            </Button>
          }
          severity="error"
        >
          Unable to load the selected member. Retry to load session history.
        </Alert>
      );
    } else if (
      isViewLoading ||
      isViewFetching ||
      selectedUser.id !== selectedUserId
    ) {
      content = (
        <CircularProgress aria-label="Loading selected member" size={24} />
      );
    } else {
      const profileTimeZone = selectedUser.profile?.timezone;
      const timeZone =
        profileTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      let validTimeZone = true;
      try {
        new Intl.DateTimeFormat('en-US', { timeZone }).format(0);
      } catch {
        validTimeZone = false;
      }
      if (!validTimeZone) {
        content = (
          <Alert severity="error">
            The selected member has an invalid timezone. Session history cannot
            be displayed.
          </Alert>
        );
      } else {
        const viewerRoles = [
          ...new Set(
            (viewer.roles ?? []).flatMap((role) => {
              if (typeof role === 'string') return [role];
              if (
                (role.resourceType === undefined ||
                  role.resourceType === null) &&
                (role.resourceId === undefined || role.resourceId === null)
              )
                return [role.name];
              return [];
            }),
          ),
        ].sort();
        content = (
          <>
            {profileTimeZone === undefined || profileTimeZone === null ? (
              <Typography role="status" variant="body2">
                Member timezone is unavailable. Using browser timezone:{' '}
                {timeZone}.
              </Typography>
            ) : (
              // MEN-5421: Ops readers otherwise take member-zone times as their own.
              <Typography color="textSecondary" variant="body2">
                Times shown in the member&apos;s timezone: {timeZone}
              </Typography>
            )}
            <AdminMemberSessions
              key={JSON.stringify([
                selectedUserId,
                viewer.id,
                viewerRoles,
                timeZone,
              ])}
              memberId={selectedUserId}
              timeZone={timeZone}
              viewerId={viewer.id}
              viewerRoles={viewerRoles}
            />
          </>
        );
      }
    }
  }
  return <SessionsLayout>{content}</SessionsLayout>;
};

export default AdminMemberSessions;
